import type { QueryClient } from '@tanstack/react-query'
import type { NotificationPayload } from '@repo/notifications/use-notifications'

export type ApplyUserCoinBalanceResult = 'ignored' | 'updated' | 'invalidated'

interface UserCoinBalanceCache {
  userBalance?: { balance?: number }
}

function toBalance(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) ? value : undefined
  }
  if (typeof value !== 'string' || value.trim() === '') {
    return undefined
  }
  const balance = Number(value)
  if (!Number.isSafeInteger(balance)) {
    return undefined
  }
  return balance
}

export function applyUserCoinBalanceNotification(
  queryClient: QueryClient,
  notification: NotificationPayload,
  currentUserId: string | undefined,
  queryKey: readonly unknown[],
): ApplyUserCoinBalanceResult {
  if (notification.userId && currentUserId && notification.userId !== currentUserId) {
    return 'ignored'
  }

  if (!notification.userId) {
    void queryClient.invalidateQueries({ queryKey })
    return 'invalidated'
  }

  if (!currentUserId) {
    return 'ignored'
  }

  const nextBalance = notification.coins?.balanceUpdated?.balance
  const balance =
    nextBalance === undefined || nextBalance === null ? undefined : toBalance(nextBalance)
  if (balance === undefined) {
    void queryClient.invalidateQueries({ queryKey })
    return 'invalidated'
  }

  queryClient.setQueriesData<UserCoinBalanceCache>({ queryKey }, current => ({
    ...current,
    userBalance: {
      ...current?.userBalance,
      balance,
    },
  }))
  return 'updated'
}
